import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import type { InsurancePlan } from '@rehome/shared';
import {
  formatHUF, useAdopterProfile, useShelterPolicy, ADOPTER_PROFILE_FIELDS,
  DEFAULT_REQUIRED_ADOPTER_FIELDS,
  upsertAdopterProfile, useAnimal, useAdoptionLeadByAnimal, toast,
  submitAdoptionRequest, finalizeAdoption, generateContractSummary,
} from '@rehome/shared';
import { supabase } from '@rehome/shared';
import {
  ShieldCheck, CreditCard, CheckCircle2, Loader2, ChevronRight,
  Gift, UserCheck, AlertTriangle, Send, Clock, Ban,
} from 'lucide-react';

const INSURANCE_PLANS: InsurancePlan[] = [
  { id: 'basic', name: 'Alapcsomag', monthlyFee: 2500, coverage: ['Baleset', 'Éves oltás'], recommended: false },
  { id: 'premium', name: 'Prémium Biztonság', monthlyFee: 5900, coverage: ['Baleset', 'Betegség', 'Műtét', 'Gyógyszerek'], recommended: true },
  { id: 'none', name: 'Nem kérek biztosítást', monthlyFee: 0, coverage: [], recommended: false },
];

const BASE_FEE = 15000;

const AdoptionCheckoutPage: React.FC = () => {
  const { animalId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isFinalize = searchParams.get('finalize') === 'true';

  const { animal: realAnimal, loading: animalLoading } = useAnimal(animalId);
  const animal = realAnimal;
  const { data: adopterProfile } = useAdopterProfile();
  const shelterId = animal?.shelterId;
  const { data: shelterPolicy } = useShelterPolicy(shelterId);
  const { lead: existingLead, loading: leadLoading } = useAdoptionLeadByAnimal(animalId);

  // Mezőlista sorrendje: állat-szintű felülbírálás → menhelyi policy →
  // platform alapértelmezés. Az utolsó lépcső nélkül egy policy nélküli
  // menhelynél a kérdőív el sem készült, és az igény leadásához elég volt a
  // telefonszám (l. DEFAULT_REQUIRED_ADOPTER_FIELDS a shared/src/types.ts-ben).
  const requiredFieldKeys: string[] = animal?.requiredAdopterFields?.length
    ? animal.requiredAdopterFields
    : shelterPolicy?.defaultRequiredFields?.length
      ? shelterPolicy.defaultRequiredFields
      : DEFAULT_REQUIRED_ADOPTER_FIELDS;
  // A menhelyi/állat-szintű sorrend helyett a kanonikus (kategóriák szerinti)
  // sorrendben jelenítjük meg, hogy a kérdőív logikus blokkokban haladjon.
  const requiredFields = ADOPTER_PROFILE_FIELDS.filter(f => requiredFieldKeys.includes(f.key));

  // Kategóriánként csoportosítva ("Lakhatás", "Háztartás", ...) — 12+ mező
  // egyetlen tagolatlan listaként végigkattinthatatlan.
  const fieldsByCategory = requiredFields.reduce<Array<{ category: string; fields: typeof requiredFields }>>(
    (acc, field) => {
      const group = acc.find(g => g.category === field.category);
      if (group) group.fields.push(field);
      else acc.push({ category: field.category, fields: [field] });
      return acc;
    },
    []
  );

  const [adopterForm, setAdopterForm] = useState<Record<string, string | boolean | number | null>>({});
  const [requirementsExpanded, setRequirementsExpanded] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  /**
   * Szabad szöveges üzenet a menhelynek.
   *
   * A tárolás és a megjelenítés OLDALA MÁR KÉSZ VOLT: az
   * `adoption_leads.message` oszlop, a `submit_adoption_request` RPC
   * `p_message` paramétere és a menhelyi bírálóoldal is kezelte — csak épp
   * senki nem küldte el, mert a felületen nem volt hol megadni.
   */
  const [applicantMessage, setApplicantMessage] = useState('');

  // Finalize mode
  const [finalizeStep, setFinalizeStep] = useState(1);
  const [selectedInsurance, setSelectedInsurance] = useState<string | null>(null);
  const [donationBoost, setDonationBoost] = useState(0);
  const [contractSummary, setContractSummary] = useState('');
  const [loadingAI, setLoadingAI] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    if (adopterProfile) {
      // Boolean fields keep '' when unanswered — `false` is a valid "Nem"
      // answer and must NOT be treated as missing.
      setAdopterForm({
        housing_type: adopterProfile.housingType ?? '',
        housing_details: adopterProfile.housingDetails ?? '',
        household_members: adopterProfile.householdMembers ?? '',
        has_children: adopterProfile.hasChildren ?? '',
        children_ages: adopterProfile.childrenAges ?? '',
        has_other_pets: adopterProfile.hasOtherPets ?? '',
        other_pets_details: adopterProfile.otherPetsDetails ?? '',
        daily_routine: adopterProfile.dailyRoutine ?? '',
        work_schedule: adopterProfile.workSchedule ?? '',
        previous_pet_experience: adopterProfile.previousPetExperience ?? '',
        previous_pet_details: adopterProfile.previousPetDetails ?? '',
        has_veterinarian: adopterProfile.hasVeterinarian ?? '',
        financial_background: adopterProfile.financialBackground ?? '',
        family_plans: adopterProfile.familyPlans ?? '',
        planned_animal_function: adopterProfile.plannedAnimalFunction ?? '',
        id_card_number: adopterProfile.idCardNumber ?? '',
      });
    }
  }, [adopterProfile]);

  const handleAdopterFieldChange = (key: string, value: string | boolean) => {
    setAdopterForm(prev => ({ ...prev, [key]: value }));
  };

  // `false` is a valid answer ("Nem") — only null/undefined/'' count as missing.
  const missingRequired = requiredFields.filter(f => {
    const val = adopterForm[f.key];
    return val === null || val === undefined || val === '';
  });

  const boolOrNull = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    await upsertAdopterProfile({
      housingType: adopterForm.housing_type as string || null,
      housingDetails: adopterForm.housing_details as string || null,
      householdMembers: adopterForm.household_members ? Number(adopterForm.household_members) : null,
      hasChildren: boolOrNull(adopterForm.has_children),
      childrenAges: adopterForm.children_ages as string || null,
      hasOtherPets: boolOrNull(adopterForm.has_other_pets),
      otherPetsDetails: adopterForm.other_pets_details as string || null,
      dailyRoutine: adopterForm.daily_routine as string || null,
      workSchedule: adopterForm.work_schedule as string || null,
      previousPetExperience: boolOrNull(adopterForm.previous_pet_experience),
      previousPetDetails: adopterForm.previous_pet_details as string || null,
      hasVeterinarian: boolOrNull(adopterForm.has_veterinarian),
      financialBackground: adopterForm.financial_background as string || null,
      familyPlans: adopterForm.family_plans as string || null,
      plannedAnimalFunction: adopterForm.planned_animal_function as string || null,
      idCardNumber: adopterForm.id_card_number as string || null,
    });
    setSavingProfile(false);
  };

  const phoneMissing = !phoneNumber.trim();
  /** Az igény csak hiánytalan kérdőívvel és telefonszámmal adható le. */
  const canSubmitRequest = !phoneMissing && missingRequired.length === 0;

  const handleSubmitRequest = async () => {
    if (!animal) return;
    if (phoneMissing) {
      toast.error('Az örökbefogadáshoz add meg a telefonszámodat.');
      return;
    }
    if (missingRequired.length > 0) {
      // A hiányzó mezők a kérdőívben pirossal jelölve; itt is megnevezzük
      // őket, mert a blokk össze lehet csukva.
      toast.error(`Hiányzó adatok: ${missingRequired.map(f => f.label).join(', ')}`);
      setRequirementsExpanded(true);
      return;
    }
    setSubmitting(true);
    await handleSaveProfile();

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error('Be kell jelentkezned.'); setSubmitting(false); navigate('/login'); return; }

    // Save phone to user_profiles
    await supabase.from('user_profiles').update({ phone: phoneNumber.trim() }).eq('id', user.id);

    const result = await submitAdoptionRequest(animal.id, user.id, applicantMessage.trim() || undefined);
    setSubmitting(false);
    if (result.success) navigate('/my-animals');
  };

  // DB CHECK constraint: insurance_plan IN ('Basic','Premium') — map UI ids.
  const INSURANCE_DB_VALUES: Record<string, string> = { basic: 'Basic', premium: 'Premium' };

  const handleFinalize = async () => {
    if (!animal || finalizing) return;
    setFinalizing(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error('Be kell jelentkezned.'); setFinalizing(false); return; }
    const result = await finalizeAdoption({
      animalId: animal.id, adopterId: user.id,
      // The fee and the extra donation are separate amounts — the RPC sums
      // them for the financial transaction. Do NOT add the boost to the fee,
      // that would double-charge the donation.
      adoptionFee: BASE_FEE, extraDonation: donationBoost,
      insurancePlan: selectedInsurance ? INSURANCE_DB_VALUES[selectedInsurance] ?? null : null,
      contractSummary: contractSummary || null, signatureData: null,
    });
    setFinalizing(false);
    if (result.success) navigate('/my-animals');
  };

  if (animalLoading) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-4 flex justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!animal) return <div className="p-8 text-center text-gray-500">Állat nem található.</div>;

  const totalFee = BASE_FEE + donationBoost;

  // ═══ FINALIZE MODE ═══
  if (isFinalize) {
    if (leadLoading) {
      return (
        <div className="max-w-3xl mx-auto py-16 px-4 flex justify-center">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      );
    }
    const canFinalize = existingLead?.status === 'Approved' && animal.status === 'AdoptionPending';
    if (!canFinalize) {
      return (
        <div className="max-w-xl mx-auto py-12 px-4 text-center space-y-4">
          <Clock className="w-16 h-16 text-amber-500 mx-auto" />
          <h1 className="text-2xl font-bold text-gray-800">Az igénylés még nincs elfogadva</h1>
          <p className="text-gray-500">A menhely még nem bírálta el az örökbefogadási igényed. Amint elfogadják, itt folytathatod.</p>
          <Link to="/my-animals" className="inline-block text-teal-600 font-semibold hover:underline">Vissza a saját állataimhoz →</Link>
        </div>
      );
    }

    return (
      <div className="max-w-3xl mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-8 px-8">
          {[1, 2, 3].map(s => (
            <div key={s} className={`w-3 h-3 rounded-full transition-all ${s <= finalizeStep ? 'bg-teal-600 scale-125' : 'bg-gray-300'}`} />
          ))}
        </div>

        {finalizeStep === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <ShieldCheck className="w-16 h-16 text-teal-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Válassz Biztonságot!</h2>
              <p className="text-gray-600">A "{animal.breed}" fajta alapján ajánljuk.</p>
            </div>
            <div className="grid gap-4">
              {INSURANCE_PLANS.map(plan => (
                <div key={plan.id} onClick={() => setSelectedInsurance(plan.id)}
                  className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${selectedInsurance === plan.id ? 'border-teal-500 bg-teal-50 shadow-lg' : 'border-gray-100 bg-white hover:border-teal-200'}`}>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold">{plan.name}</h3>
                    <span className="text-xl font-bold text-teal-700">{formatHUF(plan.monthlyFee)}<span className="text-sm font-normal text-gray-500">/hó</span></span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {plan.coverage.map(c => <span key={c} className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">{c}</span>)}
                  </div>
                  {plan.recommended && <span className="text-xs text-teal-600 font-bold mt-2 block">⭐ Ajánlott</span>}
                </div>
              ))}
            </div>
            <button onClick={() => setFinalizeStep(2)} className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2">
              Tovább <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {finalizeStep === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center space-y-6">
              <Gift className="w-16 h-16 text-rose-500 mx-auto" />
              <h2 className="text-2xl font-bold">Extra Adomány</h2>
              <p className="text-gray-600">Szeretnél extra adománnyal is segíteni a menhely munkáját?</p>
              <div className="flex justify-center gap-3 flex-wrap">
                {[1000, 3000, 5000, 10000].map(amount => (
                  <button key={amount} onClick={() => setDonationBoost(prev => prev === amount ? 0 : amount)}
                    className={`px-6 py-3 rounded-xl font-bold border-2 transition-all ${donationBoost === amount ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 hover:border-teal-300'}`}>
                    {formatHUF(amount)}
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-500">Fizetendő: <span className="text-xl font-bold text-teal-700">{formatHUF(totalFee)}</span></p>
            </div>
            <button onClick={() => { setFinalizeStep(3); setLoadingAI(true); generateContractSummary(animal.name, 'Kedves Örökbefogadó').then(s => { setContractSummary(s); setLoadingAI(false); }); }}
              className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2">
              Tovább a véglegesítéshez <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {finalizeStep === 3 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center space-y-6">
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-900">Véglegesítés</h2>
            {loadingAI ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
              <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 text-sm text-teal-900 italic">{contractSummary}</div>
            )}
            <div className="bg-gray-50 rounded-xl p-4 text-left text-sm space-y-2">
              <p><span className="font-medium">Állat:</span> {animal.name}</p>
              <p><span className="font-medium">Örökbefogadási díj:</span> {formatHUF(BASE_FEE)}</p>
              {selectedInsurance && selectedInsurance !== 'none' && (
                <p><span className="font-medium">Biztosítás:</span> {INSURANCE_PLANS.find(p => p.id === selectedInsurance)?.name}</p>
              )}
              {donationBoost > 0 && <p><span className="font-medium">Extra adomány:</span> {formatHUF(donationBoost)}</p>}
              <p className="text-lg font-bold text-teal-700">Fizetendő: {formatHUF(totalFee)}</p>
            </div>
            <button onClick={handleFinalize} disabled={finalizing} className="bg-teal-600 text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 mx-auto hover:bg-teal-700 transition-colors disabled:opacity-50">
              {finalizing ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} Véglegesítés és fizetés
            </button>
            <Link to="/my-animals" className="block text-teal-600 font-medium hover:underline">Ugrás a saját állataimhoz →</Link>
          </div>
        )}
      </div>
    );
  }

  // ═══ SUBMIT MODE ═══
  if (animal.status === 'AdoptionPending' || animal.status === 'Adopted') {
    return (
      <div className="max-w-xl mx-auto py-12 px-4 text-center space-y-4">
        <Ban className="w-16 h-16 text-gray-400 mx-auto" />
        <h1 className="text-2xl font-bold text-gray-800">Az állat nem elérhető</h1>
        <p className="text-gray-500">
          {animal.status === 'AdoptionPending' ? 'Erre az állatra már van folyamatban lévő örökbefogadási igény.' : 'Ezt az állatot már örökbe fogadták.'}
        </p>
        <Link to="/animals" className="inline-block text-teal-600 font-semibold hover:underline">Vissza a gazdikeresőkhöz →</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="space-y-6">
        {requiredFields.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <button onClick={() => setRequirementsExpanded(prev => !prev)} className="w-full flex items-center justify-between p-6 text-left">
              <div className="flex items-center gap-3">
                <UserCheck className={`w-6 h-6 ${missingRequired.length === 0 ? 'text-teal-600' : 'text-amber-500'}`} />
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Örökbefogadói kérdőív</h2>
                  <p className="text-sm text-gray-500">
                    {missingRequired.length === 0
                      ? `Mind a ${requiredFields.length} kérdés megválaszolva ✓`
                      : `${requiredFields.length - missingRequired.length} / ${requiredFields.length} kérdés kitöltve`}
                  </p>
                </div>
              </div>
              <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${requirementsExpanded ? 'rotate-90' : ''}`} />
            </button>
            {requirementsExpanded && (
              <div className="px-6 pb-6 space-y-4">
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    A menhely ezek alapján bírálja el az igényed — minden kérdés kitöltése kötelező.
                    A válaszaid a profilodba mentődnek, így egy következő igénylésnél már készen állnak.
                  </span>
                </div>
                {fieldsByCategory.map(group => (
                  <div key={group.category} className="space-y-3 pt-2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide">{group.category}</h3>
                    {group.fields.map(field => {
                  const value = adopterForm[field.key];
                  // A `false` ("Nem") ÉRVÉNYES válasz, nem hiányzó mező — a
                  // korábbi feltétel pirosra festette és csillaggal jelölte a
                  // kitöltött "Nem"-eket, miközben a beküldést engedte. A két
                  // helyen ugyanaz a szabály kell (l. missingRequired).
                  const isMissing = value === null || value === undefined || value === '';
                  const fieldDef = ADOPTER_PROFILE_FIELDS.find(f => f.key === field.key);
                  const hasOptions = fieldDef && 'options' in fieldDef && Array.isArray((fieldDef as any).options);
                  const isBooleanField = field.key.startsWith('has_') || field.key === 'previous_pet_experience';
                  if (isBooleanField) {
                    return (
                      <div key={field.key} className={`p-3 rounded-lg border ${isMissing ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                        <label className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">{field.label}{isMissing && <span className="text-red-500 ml-1">*</span>}</span>
                          <select value={value === true ? 'yes' : value === false ? 'no' : ''}
                            onChange={e => handleAdopterFieldChange(field.key, e.target.value === 'yes' ? true : e.target.value === 'no' ? false : '')}
                            className="ml-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                            <option value="">— Válassz —</option><option value="yes">Igen</option><option value="no">Nem</option>
                          </select>
                        </label>
                      </div>
                    );
                  }
                  if (hasOptions) {
                    const opts = (fieldDef as any).options as string[];
                    return (
                      <div key={field.key} className={`p-3 rounded-lg border ${isMissing ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                        <label className="block"><span className="text-sm font-medium text-gray-700">{field.label}{isMissing && <span className="text-red-500 ml-1">*</span>}</span>
                          <select value={String(value ?? '')} onChange={e => handleAdopterFieldChange(field.key, e.target.value)} className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                            <option value="">— Válassz —</option>
                            {opts.map((opt: string) => <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>)}
                          </select>
                        </label>
                      </div>
                    );
                  }
                  return (
                    <div key={field.key} className={`p-3 rounded-lg border ${isMissing ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                      <label className="block"><span className="text-sm font-medium text-gray-700">{field.label}{isMissing && <span className="text-red-500 ml-1">*</span>}</span>
                        <input type="text" value={String(value ?? '')} onChange={e => handleAdopterFieldChange(field.key, e.target.value)}
                          placeholder={`Add meg: ${field.label.toLowerCase()}`}
                          className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" />
                      </label>
                    </div>
                  );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="h-48 relative">
            <img src={animal.imageUrl || ''} className="w-full h-full object-cover" alt={animal.name} />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <h1 className="text-3xl font-bold text-white">Örökbefogadás: {animal.name}</h1>
            </div>
          </div>
          <div className="p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Adatok ellenőrzése</h2>
            <div className="grid grid-cols-2 gap-4 text-sm mb-6">
              <div><p className="text-gray-500">Fajta</p><p className="font-medium">{animal.breed}</p></div>
              <div><p className="text-gray-500">Kor</p><p className="font-medium">{animal.age} év</p></div>
              <div><p className="text-gray-500">Chip azonosító</p><p className="font-mono">{animal.microchipId || 'Nincs'}</p></div>
              <div><p className="text-gray-500">Alapdíj</p><p className="font-medium text-teal-700">{formatHUF(BASE_FEE)}</p></div>
            </div>
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefonszám <span className="text-red-500">*</span>
              </label>
              <input type="tel" value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                placeholder="+36 20 123 4567"
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none ${
                  phoneMissing ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`} />
              <p className="text-xs text-gray-500 mt-1">A menhely csak az igénylés elbírálása során, a kapcsolatfelvétel gomb megnyomása után láthatja.</p>
            </div>

            {/* Szabad szöveges üzenet — opcionális. A kérdőív strukturált
                adatot ad; itt tud a jelentkező olyat elmondani, amire nincs
                külön kérdés (miért pont ez az állat, mikor ér rá, stb.). */}
            <div className="mb-6">
              <label htmlFor="applicant-message" className="block text-sm font-medium text-gray-700 mb-1">
                Üzenet a menhelynek <span className="text-gray-400 font-normal">(opcionális)</span>
              </label>
              <textarea
                id="applicant-message"
                value={applicantMessage}
                onChange={e => setApplicantMessage(e.target.value.slice(0, 1000))}
                maxLength={1000}
                rows={4}
                placeholder={`Miért éppen ${animal.name}? Bármi, amit fontosnak tartasz elmondani a menhelynek.`}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none resize-y"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-500">Az igénylés elbírálásakor a menhely látja.</p>
                <span className={`text-xs font-mono ${applicantMessage.length >= 1000 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                  {applicantMessage.length} / 1000
                </span>
              </div>
            </div>

            {/* A gomb csak hiánytalan kérdőívvel aktív — korábban kattintható
                volt, és csak a kattintás után derült ki, hogy mi hiányzik. */}
            {!canSubmitRequest && (
              <div className="mb-3 flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                <span>
                  Az igény elküldéséhez még hiányzik:{' '}
                  <span className="font-medium text-gray-800">
                    {[...(phoneMissing ? ['Telefonszám'] : []), ...missingRequired.map(f => f.label)].join(', ')}
                  </span>
                </span>
              </div>
            )}

            <button onClick={handleSubmitRequest} disabled={submitting || !canSubmitRequest}
              className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              Örökbefogadási igény elküldése
            </button>
            <p className="text-xs text-gray-400 text-center mt-3">Az igénylés után a menhely elbírálja. Értesítünk a döntésről.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdoptionCheckoutPage;
